import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    ArrowLeft, Activity, Zap, AlertTriangle, RefreshCcw, TrendingUp, Plug,
} from "lucide-react";

/**
 * Platform-wide Connection Analytics page (Phase C.4).
 *
 * Single-pane view for ops on-call: total volume, p50/p95/p99, top
 * connections by call count, recent error types, and the live
 * circuit-breaker snapshot. Range filter + tenant scope (via
 * ``connection.logs.view`` permission tied to the admin's tenant).
 */
const RANGES = [
    { value: "1h", label: "Last 1h" },
    { value: "24h", label: "Last 24h" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
];

export default function ConnectionAnalyticsPage() {
    const { hasPermission } = useAuth();
    const canView = hasPermission("connection.logs.view");
    const [range, setRange] = useState("24h");
    const [data, setData] = useState(null);
    const [breakers, setBreakers] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const load = async () => {
        if (!canView) return;
        setLoading(true);
        setErr(null);
        try {
            const [s, b] = await Promise.all([
                api.get(`/admin/connections/_analytics/summary?range=${range}`),
                api.get("/admin/connections/_analytics/breakers"),
            ]);
            setData(s.data);
            setBreakers(b.data);
        } catch (e) {
            setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!canView) {
        return <div className="text-xs text-rose-200">Permission required: connection.logs.view</div>;
    }

    return (
        <div className="space-y-5" data-testid="connection-analytics-page">
            <div>
                <Link to="/connections" className="text-[11px] text-white/50 hover:text-white inline-flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Connections
                </Link>
                <div className="flex items-end justify-between mt-2 gap-4 flex-wrap">
                    <h1 className="text-2xl flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 opacity-70" strokeWidth={1.5} />
                        Connection Analytics
                    </h1>
                    <div className="flex items-center gap-2">
                        <select value={range} onChange={(e) => setRange(e.target.value)}
                            data-testid="analytics-range"
                            className="bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-xs">
                            {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        <button onClick={load} disabled={loading}
                            data-testid="analytics-refresh"
                            className="px-2.5 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5 inline-flex items-center gap-1.5">
                            <RefreshCcw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
                        </button>
                    </div>
                </div>
            </div>

            {err && (
                <div className="text-xs text-rose-200 inline-flex items-center gap-2"
                    data-testid="analytics-error">
                    <AlertTriangle className="w-3 h-3" /> {err}
                </div>
            )}

            {data && (
                <>
                    <KpiRow data={data} />
                    <Sparkline series={data.timeseries || []} />
                    <div className="grid lg:grid-cols-2 gap-3">
                        <TopConnections rows={data.top_connections || []} />
                        <TopErrors rows={data.top_errors || []} />
                    </div>
                </>
            )}

            {breakers && <Breakers data={breakers} />}
        </div>
    );
}

function Kpi({ label, value, hint, testId }) {
    return (
        <div className="border border-white/10 rounded-md p-3 bg-white/[0.02]"
            data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
            <div className="text-2xl font-mono mt-1">{value}</div>
            {hint && <div className="text-[10px] text-white/45 mt-1">{hint}</div>}
        </div>
    );
}

function KpiRow({ data }) {
    const t = data.totals || {};
    const successRate = t.calls ? ((t.successes / t.calls) * 100).toFixed(1) : "—";
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi label="Calls" value={(t.calls || 0).toLocaleString()} testId="kpi-calls" />
            <Kpi label="Success rate" value={`${successRate}%`}
                hint={`${(t.failures || 0).toLocaleString()} failures`}
                testId="kpi-success-rate" />
            <Kpi label="p50" value={`${data.p50}ms`} testId="kpi-p50" />
            <Kpi label="p95" value={`${data.p95}ms`} testId="kpi-p95" />
            <Kpi label="p99" value={`${data.p99}ms`} testId="kpi-p99" />
            <Kpi label="Sample size" value={(data.sample_size || 0).toLocaleString()}
                hint="duration samples used for percentiles"
                testId="kpi-sample-size" />
        </div>
    );
}

/**
 * Pure CSS sparkline — renders the timeseries as bars without dragging
 * in a charting library. Bar height = calls; failures painted in red on
 * top so a failure spike is immediately visible.
 */
function Sparkline({ series }) {
    if (!series.length) {
        return (
            <div className="border border-white/10 rounded-md p-6 text-center text-xs text-white/40"
                data-testid="analytics-sparkline-empty">
                No call activity in this range yet.
            </div>
        );
    }
    const max = Math.max(...series.map((b) => b.calls || 0), 1);
    return (
        <div className="border border-white/10 rounded-md p-3" data-testid="analytics-sparkline">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2 inline-flex items-center gap-1">
                <Activity className="w-3 h-3" /> Calls per bucket
            </div>
            <div className="flex items-end gap-0.5 h-24">
                {series.map((b, i) => {
                    const ok = (b.calls || 0) - (b.failures || 0);
                    const okH = (ok / max) * 100;
                    const failH = ((b.failures || 0) / max) * 100;
                    return (
                        <div key={i} className="flex-1 min-w-[2px] flex flex-col-reverse"
                            title={`${b.ts}: ${b.calls} calls (${b.failures} fail)`}>
                            <div style={{ height: `${okH}%` }} className="bg-emerald-300/60" />
                            <div style={{ height: `${failH}%` }} className="bg-rose-300/80" />
                        </div>
                    );
                })}
            </div>
            <div className="text-[10px] text-white/40 mt-1">
                {series[0]?.ts?.slice(0, 16).replace("T", " ")} → {series[series.length - 1]?.ts?.slice(0, 16).replace("T", " ")}
            </div>
        </div>
    );
}

function TopConnections({ rows }) {
    return (
        <div className="border border-white/10 rounded-md overflow-hidden"
            data-testid="analytics-top-connections">
            <div className="px-3 py-2 bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/50">
                Top connections
            </div>
            {rows.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-white/40">No traffic yet.</div>
            ) : (
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left px-3 py-1.5">Connection</th>
                            <th className="text-left px-3 py-1.5">Calls</th>
                            <th className="text-left px-3 py-1.5">Success</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.connection_id} className="border-t border-white/5">
                                <td className="px-3 py-1.5">
                                    <Link to={`/connections/${r.connection_id}?step=analytics`}
                                        className="hover:underline inline-flex items-center gap-1.5">
                                        <Plug className="w-3 h-3 opacity-50" />
                                        {r.name}
                                        {r.is_common && (
                                            <span className="text-[9px] px-1 border border-violet-300/30 rounded text-violet-200">COMMON</span>
                                        )}
                                    </Link>
                                </td>
                                <td className="px-3 py-1.5 font-mono">{r.calls.toLocaleString()}</td>
                                <td className={`px-3 py-1.5 font-mono ${r.success_rate < 0.95 ? "text-amber-300" : "text-emerald-300"}`}>
                                    {(r.success_rate * 100).toFixed(1)}%
                                </td>
                                <td className="px-3 py-1.5 text-[10px] text-white/45 text-right">{r.status}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function TopErrors({ rows }) {
    return (
        <div className="border border-white/10 rounded-md overflow-hidden"
            data-testid="analytics-top-errors">
            <div className="px-3 py-2 bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/50">
                Top error types
            </div>
            {rows.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-white/40">No errors. </div>
            ) : (
                <ul className="divide-y divide-white/5">
                    {rows.map((r) => (
                        <li key={r.error_type} className="px-3 py-2 flex items-center justify-between text-xs">
                            <span className="font-mono">{r.error_type}</span>
                            <span className="font-mono text-rose-200">{r.count.toLocaleString()}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Breakers({ data }) {
    const open = (data.items || []).filter((i) => i.is_open);
    const closed = (data.items || []).filter((i) => !i.is_open);
    return (
        <div className="border border-white/10 rounded-md overflow-hidden"
            data-testid="analytics-breakers">
            <div className="px-3 py-2 bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/50 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5">
                    <Zap className="w-3 h-3" /> Live circuit breakers
                </span>
                <span className={`font-mono normal-case tracking-normal ${data.open_count ? "text-rose-200" : "text-white/55"}`}>
                    {data.open_count} open · {data.items.length} total
                </span>
            </div>
            {data.items.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-white/40">No breaker state recorded yet.</div>
            ) : (
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left px-3 py-1.5">Connection</th>
                            <th className="text-left px-3 py-1.5">Endpoint</th>
                            <th className="text-left px-3 py-1.5">Failures</th>
                            <th className="text-left px-3 py-1.5">State</th>
                            <th className="text-left px-3 py-1.5">Cooldown</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[...open, ...closed].map((i, idx) => (
                            <tr key={`${i.endpoint_id}-${i.client_id}-${idx}`} className="border-t border-white/5"
                                data-testid={`breaker-row-${i.endpoint_id}`}>
                                <td className="px-3 py-1.5">
                                    <Link to={`/connections/${i.connection_id}?step=analytics`}
                                        className="hover:underline">
                                        {i.connection_name}
                                    </Link>
                                </td>
                                <td className="px-3 py-1.5 font-mono text-white/70">
                                    {i.method} {i.relative_path}
                                </td>
                                <td className={`px-3 py-1.5 font-mono ${i.failures >= i.threshold ? "text-rose-200" : "text-white/65"}`}>
                                    {i.failures} / {i.threshold}
                                </td>
                                <td className="px-3 py-1.5">
                                    {i.is_open ? (
                                        <span className="text-rose-200 inline-flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3" /> open
                                        </span>
                                    ) : (
                                        <span className="text-emerald-200">closed</span>
                                    )}
                                </td>
                                <td className="px-3 py-1.5 font-mono text-[10px] text-white/55">
                                    {i.cooldown_remaining_s ? `${i.cooldown_remaining_s}s left` : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
